/**
 * Sidequest · Friends Deck launch
 * ───────────────────────────────
 * Everything the Friends Deck launch template does after first paint. No
 * libraries, no network beyond one Shopify section request at the moment the
 * drop opens, nothing global except two custom elements.
 *
 *   1. <sqfl-launch>. The section root. It keeps its section in the right
 *      launch state as time passes, so a page left open across 18:00 turns over
 *      by itself rather than sitting on a stale panel until somebody reloads.
 *
 *      The rule for *which* state applies is not written here. It lives in
 *      snippets/sidequest-friends-launch-boot.liquid, the few lines of inline
 *      script that set the state before the browser paints, and this file calls
 *      the same function — so there is exactly one definition of "has the drop
 *      opened?" on the page and the two cannot disagree.
 *
 *      Being a custom element rather than a set of listeners is deliberate:
 *      connectedCallback and disconnectedCallback are exactly the Theme Editor's
 *      section lifecycle. A section reload, an added section, a removed one and
 *      a reordered one all arrive as connect and disconnect, so the editor needs
 *      no special handling and cannot leave a timer running behind a section
 *      that is no longer there.
 *
 *      The waitlist state is served with no purchase form in it — the page must
 *      not carry a working add-to-cart before the drop opens, not even a hidden
 *      one — so the one transition that cannot be done by flipping an attribute
 *      is waitlist → early access. That one re-fetches the section from Shopify
 *      and swaps it in. Everything after it, early access → public launch, is
 *      wording, and happens instantly.
 *
 *   2. <sqfl-sticky-bar>. The mobile purchase bar. Shown once the hero panel has
 *      scrolled away, stood down again for the final call to action and the
 *      footer, and out of the way entirely while a drawer or modal is open. It
 *      reserves its own height at the foot of the document from the moment it
 *      connects, so appearing costs no layout shift.
 *
 * Purchasing itself is not implemented here. The buy buttons are the theme's own
 * <product-form> elements, rendered by snippets/sidequest-add-to-cart.liquid and
 * driven by assets/product-form.js, so the existing cart drawer, the existing
 * cart events and Shopify's own availability rules apply unchanged.
 */

(function () {
  'use strict';

  if (window.SidequestFriendsLaunchRuntime) return;
  window.SidequestFriendsLaunchRuntime = true;

  var ROOT_TAG = 'sqfl-launch';
  var BAR_TAG = 'sqfl-sticky-bar';

  /* The shared state resolver, defined by the inline boot snippet that runs
     while the section is being parsed — always before this deferred file. The
     fallback keeps the sticky bar and the purchase buttons working if that
     snippet is ever missing; it simply stops the clock from advancing. */
  var SFL = window.SidequestFriendsLaunch || {
    now: function () {
      return Date.now();
    },
    resolve: function (el) {
      return el.getAttribute('data-sqfl-state') || 'waiting';
    },
    apply: function () {
      return false;
    },
  };

  /* How long to leave the shop alone after it has said the drop is not open
     yet. Long enough not to poll it, short enough that a page open across the
     boundary turns over promptly. */
  var STAND_DOWN_MS = 30000;

  /* ═══════════════════ 1 · the launch-state section root ═══════════════════ */

  /** Milliseconds until the next launch boundary, or Infinity when none is left. */
  function msToNextBoundary(el) {
    var now = SFL.now();
    var soonest = Infinity;

    ['data-sqfl-early', 'data-sqfl-public'].forEach(function (name) {
      var at = parseInt(el.getAttribute(name), 10);
      if (isFinite(at) && at > now) soonest = Math.min(soonest, at - now);
    });

    return soonest;
  }

  var LaunchRoot = class extends HTMLElement {
    constructor() {
      super();
      this.timer = 0;
      this.retryTimer = 0;
      this.attempts = 0;
      this.failedAt = 0;
      this.standDownUntil = 0;
      this.fetching = false;
      this.tick = this.tick.bind(this);
      this.onWake = this.onWake.bind(this);
      this.onReloadClick = this.onReloadClick.bind(this);
    }

    connectedCallback() {
      /* Fresh markup swapped in by a section reload, or by this file's own
         refresh, has not run the inline boot script — its <script> does not
         execute when markup is assigned rather than parsed. Applying here means
         a re-rendered section lands in the right state either way. */
      SFL.apply(this);

      this.addEventListener('click', this.onReloadClick);

      /* A phone that has been asleep, or a tab restored from the back-forward
         cache, can wake up on the far side of a boundary having missed every
         timer in between. These three events are when to look at the clock
         again rather than trust the schedule. */
      document.addEventListener('visibilitychange', this.onWake);
      window.addEventListener('pageshow', this.onWake);
      window.addEventListener('focus', this.onWake);

      this.tick();
    }

    disconnectedCallback() {
      this.stopTimers();
      this.removeEventListener('click', this.onReloadClick);
      document.removeEventListener('visibilitychange', this.onWake);
      window.removeEventListener('pageshow', this.onWake);
      window.removeEventListener('focus', this.onWake);
    }

    stopTimers() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = 0;
      }
      if (this.retryTimer) {
        window.clearTimeout(this.retryTimer);
        this.retryTimer = 0;
      }
    }

    onWake() {
      if (document.visibilityState === 'hidden') return;
      this.tick();
    }

    /**
     * A request is in flight, or a backoff is already counting down to one.
     * Both have to bar a fresh attempt: the timer that watches the clock runs
     * on its own schedule, and without this it can start a second cycle in the
     * gap between one attempt failing and its retry coming due.
     */
    busy() {
      return this.fetching || !!this.retryTimer;
    }

    onReloadClick(event) {
      var button = event.target && event.target.closest ? event.target.closest('[data-sqfl-reload]') : null;
      if (!button) return;
      event.preventDefault();
      window.location.reload();
    }

    tick() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = 0;
      }

      /* Quiet while the shop has recently said the drop is not open yet. The
         visitor is not told anything is happening, because nothing is. */
      var standingDown = SFL.now() < this.standDownUntil;
      var needsPurchaseMarkup = SFL.apply(this, standingDown);

      if (needsPurchaseMarkup && !standingDown) {
        /* A failed round of fetches is not the end of it: a transient network
           problem clears on its own, so the whole cycle is allowed to start
           again after a minute rather than leaving the visitor stuck. */
        if (this.failedAt && SFL.now() - this.failedAt > 60000) {
          this.attempts = 0;
          this.failedAt = 0;
          this.removeAttribute('data-sqfl-failed');
        }
        if (!this.busy() && !this.failedAt) this.refresh();
      }

      /* A forced mode is the merchant's decision and no clock overrules it; a
         page already at the public launch has no boundary left to wait for.
         Either way there is nothing further to watch, so the timer stops
         instead of ticking for the life of the tab. */
      var forced = (this.getAttribute('data-sqfl-mode') || 'auto') !== 'auto';
      var settled = this.getAttribute('data-sqfl-state') === 'public' && !needsPurchaseMarkup;
      if (forced || settled) return;

      var remaining = msToNextBoundary(this);
      var delay;
      if (!isFinite(remaining)) {
        /* Past every boundary but still short of the final state — waiting on a
           fetch. Keep looking, unhurriedly. */
        delay = 5000;
      } else if (remaining > 60000) {
        /* Far out. A coarse poll still catches a device clock being corrected
           or a laptop waking from sleep. */
        delay = 30000;
      } else {
        /* Close in. Land just past the boundary rather than a hair before it. */
        delay = Math.max(200, Math.min(remaining + 50, 1000));
      }

      this.timer = window.setTimeout(this.tick, delay);
    }

    /**
     * Ask Shopify to render this section again, now that the drop has opened,
     * and swap the result in. Shopify's Section Rendering API answers on the
     * page's own URL, so the ?view= that selects this template is carried along
     * and the purchase controls come back rendered against the live product.
     */
    refresh() {
      var sectionId = this.getAttribute('data-sqfl-section');
      if (!sectionId || typeof window.fetch !== 'function' || typeof window.DOMParser !== 'function') {
        return this.giveUp();
      }

      var url;
      try {
        url = new URL(window.location.href);
      } catch (error) {
        return this.giveUp();
      }
      url.searchParams.set('section_id', sectionId);

      this.fetching = true;
      this.attempts += 1;
      var self = this;

      window
        .fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var host = document.getElementById('shopify-section-' + sectionId);
          if (!host) throw new Error('section container missing');

          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var wrapper = parsed.getElementById('shopify-section-' + sectionId);

          var staging = document.createElement('div');
          staging.innerHTML = wrapper ? wrapper.innerHTML : parsed.body.innerHTML;

          /* The stylesheet link and the deferred script are already on the page
             and would only be duplicated; an inserted <script> does not run in
             any case. Drop both rather than grow the document each refresh. */
          staging.querySelectorAll('script, link[rel="stylesheet"]').forEach(function (node) {
            node.remove();
          });

          var incoming = staging.querySelector('[data-sqfl-section]');
          if (!incoming) throw new Error('unrecognised section markup');

          if (incoming.getAttribute('data-sqfl-rendered') !== 'purchase') {
            /* The shop answered, and its answer is that the drop is not open.
               That is the authority — this device's clock is not — so the page
               stands down and puts the waitlist state back, rather than
               insisting and eventually showing the visitor an alarm. This is
               also what quietly corrects a device whose clock runs fast. */
            self.standDown();
            return;
          }

          self.fetching = false;
          self.attempts = 0;

          /* Assigning the markup connects the new <sqfl-launch>, whose own
             connectedCallback applies the state and restarts the clock, and
             upgrades the new <product-form> so the existing cart drawer picks
             it up. This element is disconnected by the same assignment and
             cleans up after itself. */
          host.innerHTML = staging.innerHTML;
        })
        .catch(function () {
          self.fetching = false;
          /* Roughly 3s, 8s, then 20s. Long enough for a cached response to age
             out, short enough that the visitor is not left waiting. */
          var backoff = [3000, 8000, 20000][self.attempts - 1];
          if (!backoff) return self.giveUp();

          self.retryTimer = window.setTimeout(function () {
            self.retryTimer = 0;
            if (self.isConnected) self.refresh();
          }, backoff);
        });
    }

    /**
     * The shop says it is not open yet. Put the waitlist state back, say
     * nothing, and leave the shop alone for a while before asking again.
     */
    standDown() {
      this.fetching = false;
      if (this.retryTimer) {
        window.clearTimeout(this.retryTimer);
        this.retryTimer = 0;
      }
      this.attempts = 0;
      this.failedAt = 0;
      this.standDownUntil = SFL.now() + STAND_DOWN_MS;
      this.removeAttribute('data-sqfl-pending');
      this.removeAttribute('data-sqfl-failed');
      this.tick();
    }

    /**
     * The shop could not be reached at all. Rather than reload the page on a
     * loop — which is how a genuinely stuck page becomes an unusable one — say
     * plainly that the drop has opened and let the visitor press the button.
     */
    giveUp() {
      this.fetching = false;
      this.failedAt = SFL.now();
      this.setAttribute('data-sqfl-failed', 'true');
    }
  };

  if (!customElements.get(ROOT_TAG)) {
    customElements.define(ROOT_TAG, LaunchRoot);
  }

  /* ═══════════════════════ 2 · the mobile purchase bar ═════════════════════ */

  var StickyBar = class extends HTMLElement {
    constructor() {
      super();
      this.visible = false;
      this.pastPanel = false;
      this.nearEnd = false;
      this.locked = false;
      this.frame = 0;
      this.height = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onBodyMutation = this.onBodyMutation.bind(this);
    }

    connectedCallback() {
      var desktop = this.getAttribute('data-desktop') === 'true';
      this.media = window.matchMedia(desktop ? '(min-width: 0px)' : '(max-width: 749px)');

      var root = document.documentElement;
      root.classList.add('sqfl-has-bar');
      if (desktop) root.classList.add('sqfl-has-bar--desktop');

      this.measure();

      /* The hero panel is what this bar duplicates, so it is what the bar waits
         for. Falling back to any launch panel keeps it working if the merchant
         renames the anchor. */
      this.trigger =
        document.querySelector(this.getAttribute('data-watch') || '') || document.querySelector('[data-sqfl-panel]');

      /* Everything the bar must stand aside for: the final CTA and the footer. */
      this.tails = [];
      var tailSelector = this.getAttribute('data-hide-near') || '';
      if (tailSelector) {
        try {
          this.tails = Array.prototype.slice.call(document.querySelectorAll(tailSelector));
        } catch (error) {
          this.tails = [];
        }
      }

      /* State comes from measured rectangles rather than from intersection
         *events*: an observer only fires when a threshold is crossed, so an
         in-page jump, a fling scroll or a reload that restores a deep scroll
         position would never notify and the bar would sit in the wrong state.
         The observer stays as a cheap hint that something moved; the rects
         decide. */
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onGeometryChange.bind(this), { threshold: 0 });
        if (this.trigger) this.observer.observe(this.trigger);
        this.tails.forEach(
          function (tail) {
            this.observer.observe(tail);
          }.bind(this)
        );
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this);
      }

      if (this.media.addEventListener) {
        this.media.addEventListener('change', this.onMediaChange);
      } else if (this.media.addListener) {
        this.media.addListener(this.onMediaChange);
      }

      /* A drawer or modal opening is announced by the scroll lock Dawn puts on
         <body>. Watching that one signal covers third-party drawers that lock
         scrolling the same way, without hard-coding any app's selectors. */
      this.bodyObserver = new MutationObserver(this.onBodyMutation);
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });

      this.measureState();
      this.apply();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.bodyObserver) this.bodyObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      if (this.media) {
        if (this.media.removeEventListener) {
          this.media.removeEventListener('change', this.onMediaChange);
        } else if (this.media.removeListener) {
          this.media.removeListener(this.onMediaChange);
        }
      }
      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
        this.frame = 0;
      }

      /* Only give the shared custom properties back if no other bar is on the
         page — a section reload in the Theme Editor briefly has two. */
      if (!document.querySelector(BAR_TAG)) {
        var root = document.documentElement;
        root.classList.remove('sqfl-has-bar');
        root.classList.remove('sqfl-has-bar--desktop');
        root.style.removeProperty('--sqfl-bar-h');
        root.style.removeProperty('--sqfl-bar-reserve');
      }
    }

    /** The bar is laid out even while hidden, so it can be measured up front. */
    measure() {
      var height = this.offsetHeight;
      if (!height) return;
      this.height = height;
      document.documentElement.style.setProperty('--sqfl-bar-reserve', height + 'px');
    }

    onScroll() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(
        function () {
          this.frame = 0;
          this.onGeometryChange();
        }.bind(this)
      );
    }

    onResize() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(
        function () {
          this.frame = 0;
          this.measure();
          this.measureState();
          this.apply();
        }.bind(this)
      );
    }

    onMediaChange() {
      this.apply();
    }

    onBodyMutation() {
      var body = document.body;
      var locked =
        body.classList.contains('overflow-hidden') ||
        body.classList.contains('overflow-hidden-mobile') ||
        body.classList.contains('overflow-hidden-tablet') ||
        body.style.overflow === 'hidden';

      if (locked === this.locked) return;
      this.locked = locked;
      this.apply();
    }

    onGeometryChange() {
      this.measureState();
      this.apply();
    }

    /** Where things actually are, right now. */
    measureState() {
      if (this.trigger) {
        /* Past the panel means it has left the top of the viewport entirely, so
           scrolling back up to it puts the bar away again. */
        this.pastPanel = this.trigger.getBoundingClientRect().bottom < 0;
      } else {
        this.pastPanel = window.scrollY > window.innerHeight;
      }

      this.nearEnd = this.tails.some(function (tail) {
        var rect = tail.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      });
    }

    apply() {
      var shouldShow = this.pastPanel && !this.nearEnd && !this.locked && this.media.matches;

      if (shouldShow !== this.visible) {
        this.visible = shouldShow;
        this.setAttribute('data-visible', shouldShow ? 'true' : 'false');
      }

      document.documentElement.style.setProperty('--sqfl-bar-h', shouldShow ? this.height + 'px' : '0px');
    }
  };

  if (!customElements.get(BAR_TAG)) {
    customElements.define(BAR_TAG, StickyBar);
  }
})();
